import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircleIcon, PlayCircleIcon, ClockIcon, TagIcon, ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
// Mock tutorial data with extended content
const tutorialData = [{
  id: 1,
  title: 'Getting Started with Payment Requests',
  description: 'Learn how to create and manage payment requests for your customers.',
  category: 'Basics',
  duration: '5 min',
  completed: true,
  completedAt: '2023-11-15T14:30:00',
  icon: 'LayoutGridIcon',
  content: `
      <h2>Introduction to Payment Requests</h2>
      <p>Payment requests allow you to create and send professional payment links to your customers. They're perfect for accepting payments without the need for a physical terminal or when dealing with remote customers.</p>
      
      <h2>Creating Your First Payment Request</h2>
      <p>To create a payment request:</p>
      <ol>
        <li>Navigate to the Payment Requests section in the sidebar</li>
        <li>Click the "New Payment Request" button in the top right</li>
        <li>Fill in the customer details, amount, and description</li>
        <li>Choose your preferred delivery method (SMS, email, or generate a link)</li>
        <li>Click "Send Payment Request"</li>
      </ol>
      
      <h2>Managing Existing Requests</h2>
      <p>All your payment requests are displayed in a list view, showing their status (pending, paid, expired). You can:</p>
      <ul>
        <li>Filter requests by status</li>
        <li>Search for specific requests</li>
        <li>Resend payment links</li>
        <li>Cancel pending requests</li>
        <li>View detailed payment information</li>
      </ul>
      
      <h2>Setting Up Recurring Payments</h2>
      <p>For regular customers or subscription services, you can set up recurring payment requests:</p>
      <ol>
        <li>Create a new payment request</li>
        <li>Enable the "Recurring" toggle</li>
        <li>Set the frequency (weekly, monthly, etc.)</li>
        <li>Choose an end date or number of occurrences</li>
      </ol>
      
      <h2>Best Practices</h2>
      <p>To maximize payment completion rates:</p>
      <ul>
        <li>Use clear, specific descriptions</li>
        <li>Set reasonable expiration dates</li>
        <li>Include your branding in the payment page</li>
        <li>Send friendly reminders for pending requests</li>
      </ul>
    `,
  steps: [{
    id: 1,
    title: 'Navigate to Payment Requests',
    completed: true
  }, {
    id: 2,
    title: 'Create your first request',
    completed: true
  }, {
    id: 3,
    title: 'Send the request to a customer',
    completed: true
  }, {
    id: 4,
    title: 'View payment status',
    completed: true
  }, {
    id: 5,
    title: 'Set up a recurring payment',
    completed: true
  }]
}, {
  id: 2,
  title: 'Setting Up Your Virtual Terminal',
  description: 'Configure your virtual terminal for accepting card payments.',
  category: 'Terminals',
  duration: '8 min',
  completed: true,
  completedAt: '2023-11-20T10:15:00',
  icon: 'BanknoteIcon',
  content: `
      <h2>Understanding Virtual Terminals</h2>
      <p>A virtual terminal turns your computer or tablet into a payment processing terminal, allowing you to manually enter card details for payments when the physical card is not present.</p>
      
      <h2>Accessing Your Virtual Terminal</h2>
      <p>To access your virtual terminal:</p>
      <ol>
        <li>Navigate to the Terminals section in the sidebar</li>
        <li>Select "Virtual" from the submenu</li>
        <li>The virtual terminal interface will load automatically</li>
      </ol>
      
      <h2>Processing a Payment</h2>
      <p>To process a payment through your virtual terminal:</p>
      <ol>
        <li>Enter the payment amount</li>
        <li>Add a description or reference (optional)</li>
        <li>Enter the customer's card details (number, expiry date, CVV)</li>
        <li>Add customer information if needed</li>
        <li>Click "Process Payment"</li>
        <li>Verify the transaction result</li>
      </ol>
      
      <h2>Security Best Practices</h2>
      <p>When handling card information:</p>
      <ul>
        <li>Never store card details</li>
        <li>Ensure you have customer consent to process the payment</li>
        <li>Use the virtual terminal in a private environment</li>
        <li>Always verify the cardholder's identity for larger transactions</li>
        <li>Log out of your account when not in use</li>
      </ul>
      
      <h2>Transaction Records</h2>
      <p>All virtual terminal transactions are recorded in your transaction history. You can:</p>
      <ul>
        <li>Issue refunds if needed</li>
        <li>Send receipts to customers</li>
        <li>View transaction details</li>
        <li>Export transaction reports</li>
      </ul>
    `,
  steps: [{
    id: 1,
    title: 'Access the virtual terminal',
    completed: true
  }, {
    id: 2,
    title: 'Configure terminal settings',
    completed: true
  }, {
    id: 3,
    title: 'Process your first payment',
    completed: true
  }, {
    id: 4,
    title: 'Send a receipt',
    completed: true
  }, {
    id: 5,
    title: 'Review security practices',
    completed: true
  }]
}, {
  id: 3,
  title: 'Creating Your First Invoice',
  description: 'Learn how to create, customize and send professional invoices.',
  category: 'Invoicing',
  duration: '6 min',
  completed: false,
  completedAt: null,
  icon: 'FileTextIcon',
  content: `
      <h2>Introduction to Invoicing</h2>
      <p>The invoicing system allows you to create, send, and manage professional invoices for your customers. Invoices can be paid online and automatically reconciled with your accounts.</p>
      
      <h2>Creating a New Invoice</h2>
      <p>To create your first invoice:</p>
      <ol>
        <li>Navigate to the Invoices section in the sidebar</li>
        <li>Click the "Create Invoice" button</li>
        <li>Select an existing customer or add a new one</li>
        <li>Add invoice items, quantities, and prices</li>
        <li>Set payment terms and due date</li>
        <li>Add any notes or payment instructions</li>
        <li>Preview the invoice</li>
        <li>Click "Send Invoice" or save as draft</li>
      </ol>
      
      <h2>Customizing Your Invoices</h2>
      <p>Personalize your invoices to match your brand:</p>
      <ul>
        <li>Add your company logo</li>
        <li>Choose a color scheme</li>
        <li>Customize the invoice template</li>
        <li>Add terms and conditions</li>
        <li>Include your business details and payment information</li>
      </ul>
      
      <h2>Managing Invoices</h2>
      <p>After creating invoices, you can:</p>
      <ul>
        <li>Track payment status</li>
        <li>Send reminders for overdue invoices</li>
        <li>Record manual payments</li>
        <li>Issue credit notes or refunds</li>
        <li>Generate invoice reports</li>
      </ul>
      
      <h2>Recurring Invoices</h2>
      <p>For regular billing, set up recurring invoices:</p>
      <ol>
        <li>Create a new invoice</li>
        <li>Enable the "Make Recurring" option</li>
        <li>Set the frequency and duration</li>
        <li>The system will automatically generate and send invoices according to your schedule</li>
      </ol>
    `,
  steps: [{
    id: 1,
    title: 'Navigate to Invoices section',
    completed: false
  }, {
    id: 2,
    title: 'Create your first invoice',
    completed: false
  }, {
    id: 3,
    title: 'Customize the invoice template',
    completed: false
  }, {
    id: 4,
    title: 'Send the invoice to a customer',
    completed: false
  }, {
    id: 5,
    title: 'Set up a recurring invoice',
    completed: false
  }]
},
// Additional tutorials would be defined here with their full content
{
  id: 4,
  title: 'Managing Customer Profiles',
  description: 'Organize and manage your customer information efficiently.',
  category: 'Customers',
  duration: '4 min',
  completed: true,
  completedAt: '2023-12-01T09:45:00',
  icon: 'UsersIcon',
  content: `
      <h2>Introduction to Customer Management</h2>
      <p>Effective customer management helps you maintain relationships and streamline your payment processes. The customer profiles feature allows you to store and organize all customer information in one place.</p>
      
      <h2>Creating Customer Profiles</h2>
      <p>To add a new customer to your database:</p>
      <ol>
        <li>Navigate to the Customers section</li>
        <li>Click "Add New Customer"</li>
        <li>Enter the customer's details (name, contact information, etc.)</li>
        <li>Add any custom fields relevant to your business</li>
        <li>Save the customer profile</li>
      </ol>
      
      <h2>Managing Customer Information</h2>
      <p>With customer profiles, you can:</p>
      <ul>
        <li>Store multiple contact methods (email, phone, address)</li>
        <li>Save payment preferences</li>
        <li>View transaction history</li>
        <li>Add notes and custom fields</li>
        <li>Organize customers with tags and groups</li>
      </ul>
      
      <h2>Using Customer Data in Transactions</h2>
      <p>Customer profiles integrate with other platform features:</p>
      <ul>
        <li>Quickly select customers when creating invoices</li>
        <li>Auto-fill information for payment requests</li>
        <li>Track customer spending and preferences</li>
        <li>Set up customer-specific discounts or terms</li>
      </ul>
      
      <h2>Customer Privacy and GDPR Compliance</h2>
      <p>When managing customer data, ensure you:</p>
      <ul>
        <li>Only collect necessary information</li>
        <li>Obtain appropriate consent</li>
        <li>Provide access to stored data when requested</li>
        <li>Implement the right to be forgotten</li>
        <li>Keep customer data secure</li>
      </ul>
    `,
  steps: [{
    id: 1,
    title: 'Navigate to Customers section',
    completed: true
  }, {
    id: 2,
    title: 'Create a new customer profile',
    completed: true
  }, {
    id: 3,
    title: 'Add custom fields',
    completed: true
  }, {
    id: 4,
    title: 'Use customer data in transactions',
    completed: true
  }, {
    id: 5,
    title: 'Review privacy practices',
    completed: true
  }]
}, {
  id: 5,
  title: 'Understanding Transaction Reports',
  description: 'Learn how to view and interpret your transaction reports.',
  category: 'Reports',
  duration: '10 min',
  completed: false,
  completedAt: null,
  icon: 'BarChartIcon',
  content: `<p>Full tutorial content for Understanding Transaction Reports...</p>`,
  steps: [{
    id: 1,
    title: 'Navigate to Reports section',
    completed: false
  }, {
    id: 2,
    title: 'View your first transaction report',
    completed: false
  }, {
    id: 3,
    title: 'Apply filters and date ranges',
    completed: false
  }, {
    id: 4,
    title: 'Export a report',
    completed: false
  }, {
    id: 5,
    title: 'Set up scheduled reports',
    completed: false
  }]
}];
export function TutorialDetail() {
  const {
    t
  } = useTranslation("tutorials");
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const tutorialId = parseInt(id || '1');
  // Find the current tutorial
  const tutorial = tutorialData.find(t => t.id === tutorialId);
  // Find previous and next tutorials for navigation
  const prevTutorial = tutorialData.find(t => t.id === tutorialId - 1);
  const nextTutorial = tutorialData.find(t => t.id === tutorialId + 1);
  // State for tutorial completion
  const [isCompleted, setIsCompleted] = useState(tutorial?.completed || false);
  const [stepsCompleted, setStepsCompleted] = useState<number[]>([]);
  // Initialize steps completion state
  useEffect(() => {
    if (tutorial) {
      setIsCompleted(tutorial.completed);
      setStepsCompleted(tutorial.steps.filter(step => step.completed).map(step => step.id));
    }
  }, [tutorial]);
  // Handle step completion toggle
  const toggleStepCompletion = (stepId: number) => {
    if (stepsCompleted.includes(stepId)) {
      setStepsCompleted(stepsCompleted.filter(id => id !== stepId));
    } else {
      setStepsCompleted([...stepsCompleted, stepId]);
    }
  };
  // Handle mark tutorial as complete/incomplete
  const toggleTutorialCompletion = () => {
    setIsCompleted(!isCompleted);
  };
  if (!tutorial) {
    return <div className="flex flex-col items-center justify-center py-12" data-id="element-15064" data-mp="1o8rsup8huses">
        <h2 className="text-2xl font-semibold text-gray-900 mb-2" data-id="element-15065" data-mp="220zbze6uukv0">
          {t("pages.TutorialDetail.tutorial_not_found")}
        </h2>
        <p className="text-gray-500 mb-6" data-id="element-15066" data-mp="1y31lm81clsq1">
          {t("pages.TutorialDetail.the_tutorial_youre_looking_for_doesnt")}
        </p>
        <button onClick={() => navigate('/tutorials')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-sm font-medium" data-id="element-15067" data-mp="271vo2f1d1dbi">
          
          <ArrowLeftIcon size={16} className="mr-1" data-id="element-15068" data-mp="2qlb3933i8k2s" />
          {t("pages.TutorialDetail.back_to_tutorials")}
        </button>
      </div>;
  }
  return <div className="max-w-4xl mx-auto" data-id="element-15069" data-mp="2gnxzwz6i6x4z">
      {/* Back button */}
      <button onClick={() => navigate('/tutorials')} className="flex items-center text-gray-900 hover:text-gray-600 transition-colors text-lg font-semibold mb-6" data-id="element-15070" data-mp="2lo2kp1eg2e10">
        
        <ArrowLeftIcon size={20} className="mr-2" data-id="element-15071" data-mp="27uky17qgol6r" />
        {t("pages.TutorialDetail.back_to_tutorials")}
      </button>

      {/* Tutorial header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6" data-id="element-15072" data-mp="1xz9japf1zdz0">
        <div className="flex items-start justify-between mb-4" data-id="element-15073" data-mp="22x1k68xoeafc">
          <div className="flex items-start" data-id="element-15074" data-mp="22vgmwbjz8dds">
            <div data-id="element-15075" data-mp="1oxjs9kp32h1r">
              <h1 className="text-2xl font-semibold text-gray-900" data-id="element-15076" data-mp="1oz37r92x0o2h">
                {tutorial.title}
              </h1>
              <div className="flex items-center mt-2 space-x-4" data-id="element-15077" data-mp="1sylr40f38r37">
                <div className="flex items-center text-sm text-gray-500" data-id="element-15078" data-mp="1lv5k9sij8br9">
                  <TagIcon size={14} className="mr-1" data-id="element-15079" data-mp="1gwkhblbwbdz4" />
                  {tutorial.category}
                </div>
                <div className="flex items-center text-sm text-gray-500" data-id="element-15080" data-mp="1c5lyadziw1s6">
                  <ClockIcon size={14} className="mr-1" data-id="element-15081" data-mp="177kfzv9x86hr" />
                  {tutorial.duration}
                </div>
              </div>
            </div>
          </div>
          <div className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${isCompleted ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-gray-100 text-gray-600 border border-gray-200'}`} data-id="element-15082" data-mp="133mghjgooto6">
            
            {isCompleted ? 'Completed' : 'Not completed'}
          </div>
        </div>
        <p className="text-sm text-gray-500 mb-6" data-id="element-15083" data-mp="2nlkc0v5nbbzq">{tutorial.description}</p>
        <button onClick={toggleTutorialCompletion} className={`flex items-center text-sm font-medium rounded-lg px-4 py-2 transition-colors shadow-sm ${isCompleted ? 'bg-gray-100 text-gray-700 hover:bg-gray-200' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-15084" data-mp="2njgbmn6ygv47">
          
          {isCompleted ? <>
              <CheckCircleIcon size={16} className="mr-2" data-id="element-15085" data-mp="2v2z4dl6042pz" />
              {t("pages.TutorialDetail.mark_as_incomplete")}
            </> : <>
              <CheckCircleIcon size={16} className="mr-2" data-id="element-15086" data-mp="34v4s2ip7zxzd" />
              {t("pages.TutorialDetail.mark_as_complete")}
            </>}
        </button>
      </div>

      {/* Tutorial content and steps */}
      <div className="flex flex-col md:flex-row gap-6" data-id="element-15087" data-mp="2c5fqa34ortwp">
        {/* Main content */}
        <div className="md:w-2/3" data-id="element-15088" data-mp="2h4r9disizbhg">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6" data-id="element-15089" data-mp="2h4ixnu852c7u">
            <div className="prose max-w-none prose-headings:font-semibold prose-headings:text-gray-900 prose-p:text-gray-600 prose-li:text-gray-600" dangerouslySetInnerHTML={{
            __html: tutorial.content
          }} data-id="element-15090" data-mp="2m5f166okvwcq" />
            
          </div>

          {/* Tutorial navigation */}
          <div className="flex justify-between mt-6" data-id="element-15091" data-mp="37udejxpn03ck">
            {prevTutorial ? <button onClick={() => navigate(`/tutorial/${prevTutorial.id}`)} className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors" data-id="element-15092" data-mp="32wk60y0v2x94">
              
                <ArrowLeftIcon size={16} className="mr-2" data-id="element-15093" data-mp="3curd8p5cm0xx" />
                {t("pages.TutorialDetail.previous")} {prevTutorial.title}
              </button> : <div data-id="element-15094" data-mp="2p5f41pa0tsvm"></div>}
            {nextTutorial && <button onClick={() => navigate(`/tutorial/${nextTutorial.id}`)} className="flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors" data-id="element-15095" data-mp="2k5u69i6l7qkh">
              
                {t("pages.TutorialDetail.next")} {nextTutorial.title}
                <ArrowRightIcon size={16} className="ml-2" data-id="element-15096" data-mp="2u2p1ns7kdauj" />
              </button>}
          </div>
        </div>

        {/* Steps sidebar */}
        <div className="md:w-1/3" data-id="element-15097" data-mp="21fkr77ppc4bd">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-6" data-id="element-15098" data-mp="21ejgj4cllju6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wider" data-id="element-15099" data-mp="gwid86ifokkg">
              {t("pages.TutorialDetail.tutorial_steps")}
            </h3>
            <div className="space-y-3" data-id="element-15100" data-mp="6zmf8uq5vgu5">
              {tutorial.steps.map(step => <div key={step.id} className="flex items-start" data-id="element-15101" data-mp="6x9mrnms1o2u">
                  <button onClick={() => toggleStepCompletion(step.id)} className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded-full border ${stepsCompleted.includes(step.id) ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300'} flex items-center justify-center transition-colors`} data-id="element-15102" data-mp="byyxf2637zqe">
                  
                    {stepsCompleted.includes(step.id) && <CheckCircleIcon size={14} className="text-white" data-id="element-15103" data-mp="uoos7p9uolib" />}
                  </button>
                  <span className="ml-3 text-sm text-gray-700" data-id="element-15104" data-mp="3bdc7fkcq5t8i">
                    {step.title}
                  </span>
                </div>)}
            </div>
            <div className="mt-6" data-id="element-15105" data-mp="3fgh5naprv8rc">
              <div className="w-full bg-gray-100 rounded-full h-1.5" data-id="element-15106" data-mp="ax373w4jq7t7">
                <div className="bg-green-500 h-1.5 rounded-full transition-all duration-300 ease-in-out" style={{
                width: `${stepsCompleted.length / tutorial.steps.length * 100}%`
              }} data-id="element-15107" data-mp="ayecu1hjxpmy">
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2 font-medium" data-id="element-15108" data-mp="3t39a48q95mj1">
                {stepsCompleted.length} {t("pages.TutorialDetail.of")} {tutorial.steps.length} {t("pages.TutorialDetail.steps_completed")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}